import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ImageUpload from './ImageUpload';
import Button from './Button';
import '../ui/Modal.css';

/** Opciones de duración → solo UX; la BD guarda fechaInicio/fechaFin. */
const DURACIONES = [
  { key: '1d',  lbl: '1 día' },
  { key: '1w',  lbl: '1 semana' },
  { key: '15d', lbl: '15 días' },
  { key: '1m',  lbl: '1 mes' },
  { key: '3m',  lbl: '3 meses' },
  { key: '6m',  lbl: '6 meses' },
  { key: '1y',  lbl: '1 año' },
  { key: 'custom', lbl: 'Personalizado' },
];

/**
 * Calcula fechaFin a partir de fechaInicio + duración.
 * Regla consistente: se suma la duración al día de inicio y se deja
 * en 23:59:59.999 (el backend además re-normaliza a fin de día).
 * "1 día" = solo el día de inicio (00:00 → 23:59:59).
 */
function calcularFechaFin(fechaInicioISO, duracion) {
  if (!fechaInicioISO || duracion === 'custom') return '';
  const d = new Date(fechaInicioISO + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  switch (duracion) {
    case '1w':  d.setDate(d.getDate() + 7); break;
    case '15d': d.setDate(d.getDate() + 15); break;
    case '1m':  d.setMonth(d.getMonth() + 1); break;
    case '3m':  d.setMonth(d.getMonth() + 3); break;
    case '6m':  d.setMonth(d.getMonth() + 6); break;
    case '1y':  d.setFullYear(d.getFullYear() + 1); break;
    case '1d':
    default:
      break;
  }
  d.setHours(23, 59, 59, 999);
  return d.toISOString().slice(0, 10);
}

function toInputDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Fecha local del admin en formato date input
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSafeHttpUrl(val) {
  if (typeof val !== 'string' || !val.trim()) return false;
  try {
    const u = new URL(val.trim());
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    if (u.username || u.password) return false;
    const host = u.hostname.toLowerCase();
    if (host === 'localhost') return true;
    const parts = host.split('.');
    const tld = parts[parts.length - 1];
    return parts.length >= 2 && tld.length >= 2 && /^[a-z]+$/.test(tld);
  } catch {
    return false;
  }
}

const MULTIPART = { 'Content-Type': 'multipart/form-data' };

export default function ModalSponsor({ sponsor, onClose, onSaved }) {
  const esEdicion = Boolean(sponsor?._id);

  const [nombre, setNombre] = useState(sponsor?.nombre || '');
  const [targetUrl, setTargetUrl] = useState(sponsor?.targetUrl || '');
  const [mensajeAporte, setMensajeAporte] = useState(sponsor?.mensajeAporte || '');
  const [delaySegundos, setDelaySegundos] = useState(
    String(sponsor?.delaySegundos ?? 60)
  );
  const [fechaInicio, setFechaInicio] = useState(toInputDate(sponsor?.fechaInicio) || '');
  const [duracion, setDuracion] = useState(esEdicion ? 'custom' : '1m');
  const [fechaFinCustom, setFechaFinCustom] = useState(toInputDate(sponsor?.fechaFin) || '');
  const [active, setActive] = useState(sponsor?.active !== false);
  const [imagen, setImagen] = useState(null); // File nuevo
  const [saving, setSaving] = useState(false);

  const fechaFinCalc = useMemo(() => {
    if (duracion === 'custom') return fechaFinCustom;
    return calcularFechaFin(fechaInicio, duracion);
  }, [duracion, fechaInicio, fechaFinCustom]);

  // Si cambia la fecha de inicio con una duración fija, recalcular fin.
  useEffect(() => {
    if (duracion !== 'custom' && fechaInicio) {
      setFechaFinCustom(calcularFechaFin(fechaInicio, duracion));
    }
  }, [fechaInicio, duracion]);

  const previewSrc = imagen
    ? URL.createObjectURL(imagen)
    : sponsor?.imageUrl || '';

  const validar = () => {
    if (!nombre.trim()) return 'El nombre es obligatorio';
    if (!isSafeHttpUrl(targetUrl)) return 'La URL debe ser http(s) válida';
    if (!fechaInicio) return 'La fecha de inicio es obligatoria';
    if (!fechaFinCalc) return 'La fecha de fin es obligatoria';
    if (new Date(fechaFinCalc) <= new Date(fechaInicio + 'T00:00:00')) {
      return 'La fecha de fin debe ser posterior a la de inicio';
    }
    if (!esEdicion && !imagen && !sponsor?.imageUrl) {
      return 'La imagen es obligatoria al crear';
    }
    const delay = Number(delaySegundos);
    if (!Number.isFinite(delay) || delay < 1 || delay > 3600) {
      return 'El delay del popup debe estar entre 1 y 3600 segundos';
    }
    if (imagen) {
      if (!imagen.type.startsWith('image/')) return 'Solo se permiten imágenes';
      if (imagen.size > 5 * 1024 * 1024) return 'La imagen no puede exceder 5MB';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validar();
    if (err) return toast.error(err);

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('nombre', nombre.trim());
      fd.append('targetUrl', targetUrl.trim());
      fd.append('mensajeAporte', mensajeAporte.trim());
      fd.append('delaySegundos', String(Math.round(Number(delaySegundos))));
      fd.append('fechaInicio', new Date(fechaInicio + 'T00:00:00').toISOString());
      fd.append('fechaFin', new Date(fechaFinCalc + 'T00:00:00').toISOString());
      fd.append('active', String(active));
      if (imagen) fd.append('imagen', imagen);

      if (esEdicion) {
        await api.put(`/auspiciantes/${sponsor._id}`, fd, { headers: MULTIPART });
        toast.success('Auspiciante actualizado');
      } else {
        await api.post('/auspiciantes', fd, { headers: MULTIPART });
        toast.success('Auspiciante creado');
      }
      onSaved?.();
      onClose();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.msg || data?.errors?.[0]?.mensaje || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <button className="modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
        <h2>{esEdicion ? '✏️ Editar Auspiciante' : '🏢 Nuevo Auspiciante'}</h2>
        <p className="modal-sub">
          {esEdicion
            ? 'Modifica los datos. Para recontratar un vencido, ajusta las fechas de vigencia.'
            : 'Registra un patrocinador con su vigencia y mensaje de aporte.'}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="f-group">
            <label className="f-lbl" htmlFor="sp-nombre">Nombre del auspiciante *</label>
            <input
              id="sp-nombre"
              className="input"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={120}
              required
              placeholder="Ej: Las Aulas"
            />
          </div>

          <div className="f-group">
            <ImageUpload
              label="Imagen / banner *"
              name="imagen"
              currentImage={sponsor?.imageUrl || ''}
              onFileSelect={setImagen}
            />
            {!esEdicion && !imagen && (
              <p className="f-err">La imagen es obligatoria al crear.</p>
            )}
          </div>

          <div className="f-group">
            <label className="f-lbl" htmlFor="sp-url">Página del auspiciante *</label>
            <input
              id="sp-url"
              className="input"
              type="url"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              required
              placeholder="https://www.ejemplo.com"
            />
          </div>

          <div className="f-group">
            <label className="f-lbl" htmlFor="sp-msj">¿Qué aportaron a RedCimarrona? *</label>
            <textarea
              id="sp-msj"
              className="input"
              rows={3}
              value={mensajeAporte}
              onChange={(e) => setMensajeAporte(e.target.value)}
              maxLength={500}
              required
              placeholder="Ej: hospedaje y dominio por un año"
            />
            <small style={{ color: 'var(--txs)', fontSize: 12 }}>
              {mensajeAporte.length}/500 — en el popup se verá: <strong>“Apoyaron con…” + este texto</strong>
            </small>
          </div>

          <div className="f-group">
            <label className="f-lbl" htmlFor="sp-delay">Popup aparece a los (segundos) *</label>
            <input
              id="sp-delay"
              className="input"
              type="number"
              min={1}
              max={3600}
              step={1}
              value={delaySegundos}
              onChange={(e) => setDelaySegundos(e.target.value)}
              required
            />
            <small style={{ color: 'var(--txs)', fontSize: 12 }}>
              Segundos de espera antes de mostrar el popup (1–3600). Por defecto: 60.
            </small>
          </div>

          <div className="f-row">
            <div className="f-group">
              <label className="f-lbl" htmlFor="sp-inicio">Inicio de publicación *</label>
              <input
                id="sp-inicio"
                className="input"
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                required
              />
            </div>
            <div className="f-group">
              <label className="f-lbl" htmlFor="sp-dur">Duración</label>
              <select
                id="sp-dur"
                className="input"
                value={duracion}
                onChange={(e) => setDuracion(e.target.value)}
              >
                {DURACIONES.map((d) => (
                  <option key={d.key} value={d.key}>{d.lbl}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="f-row">
            <div className="f-group">
              <label className="f-lbl" htmlFor="sp-fin">
                {duracion === 'custom' ? 'Fin de publicación *' : 'Fin calculado'}
              </label>
              <input
                id="sp-fin"
                className="input"
                type="date"
                value={toInputDate(fechaFinCalc ? new Date(fechaFinCalc + 'T12:00:00') : '')}
                onChange={(e) => {
                  setDuracion('custom');
                  setFechaFinCustom(e.target.value);
                }}
                readOnly={duracion !== 'custom'}
                required
              />
            </div>
            <div className="f-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label className="f-lbl" htmlFor="sp-act" style={{ marginBottom: 8 }}>
                <input
                  id="sp-act"
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  style={{ marginRight: 8, width: 18, height: 18, verticalAlign: 'middle' }}
                />
                Activo
              </label>
              <p style={{ fontSize: 12, color: 'var(--txs)', margin: 0 }}>
                Un sponsor inactivo nunca aparece, aunque esté vigente.
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-5" style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <Button variant="ghost" onClick={onClose} disabled={saving} style={{ flex: 1 }}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" loading={saving} style={{ flex: 2 }}>
              {esEdicion ? 'Guardar cambios' : 'Guardar auspiciante'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
